import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { transactionApi } from '../services/api';
import './TransactionPage.css'; // Reuse the same CSS
import { formatNumberWithCommas } from '../utils/numberFormatting';

function AuthorizerPage() {
  // --- NEW: Numeric input state for editing/creating transactions ---
  const [editForm, setEditForm] = useState({});
  // Add more fields as needed (amount, price, yield, commission, brokerage, interestRate, principal_amount, maturity_value, etc.)

  // Helper to strip commas from formatted numbers
  const stripCommas = (val) => (typeof val === 'string' ? val.replace(/,/g, '') : val);

  // Handler for numeric inputs (amount, price, etc.)
  const handleNumericInput = (e) => {
    const { name, value } = e.target;
    // Remove all commas for parsing
    const raw = stripCommas(value);
    // Only allow valid number input (empty or valid number)
    if (raw === '' || /^-?\d*(\.\d*)?$/.test(raw)) {
      // Format with commas as you type
      let formatted = raw;
      if (raw !== '' && !isNaN(raw)) {
        // Truncate to 4 decimals, then format
        formatted = formatNumberWithCommas(raw, 4);
      }
      setEditForm((prev) => ({ ...prev, [name]: formatted }));
    }
  };

  // When submitting, strip commas and parse numbers as needed
  const getSanitizedEditForm = () => {
    const sanitized = { ...editForm };
    ['amount', 'price', 'yield', 'commission', 'brokerage', 'interestRate', 'principal_amount', 'maturity_value'].forEach((field) => {
      if (sanitized[field] !== undefined) {
        const raw = stripCommas(sanitized[field]);
        sanitized[field] = raw === '' ? null : parseFloat(raw);
      }
    });
    return sanitized;
  };


  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [comment, setComment] = useState('');

  // Function to fetch all transactions
  const fetchTransactions = async () => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      const data = await transactionApi.getAll();
      console.log('Fetched transactions:', data);
      
      // Set status if not already present
      const transactionsWithStatus = data.map(tx => ({
        ...tx,
        status: tx.status || 'pending'
      }));
      setTransactions(transactionsWithStatus);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle authorize/reject action
  const handleAuthorization = async (id, status) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      // Require comment for rejection
      if (status === 'rejected' && !comment) {
        setError('Comment is required when rejecting a transaction');
        setIsLoading(false);
        return;
      }
      
      // Get user from localStorage for auth header
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Call the API to update the transaction status
      const result = await transactionApi.update(id, { 
        status,
        comment: status === 'rejected' ? comment : ''
      }, {
        headers: {
          'x-user-data': JSON.stringify(user)
        }
      });
      
      // Update local state
      setTransactions(prevTransactions => 
        prevTransactions.map(tx => 
          tx.id === id ? { ...tx, status } : tx
        )
      );
      
      setSuccessMessage(`Transaction ${id} has been ${status} successfully.`);
      setComment('');
    } catch (err) {
      console.error('Authorization error:', err);
      if (err.response && err.response.status === 403) {
        setError('You do not have permission to authorize transactions');
      } else {
        setError(`Failed to ${status} transaction. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle view detail click
  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };

  // Load transactions when component mounts
  useEffect(() => {
    fetchTransactions();
  }, []);

  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd');
    } catch (e) {
      return dateString;
    }
  };

  // Check if user has authorizer role
  const isAuthorizer = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user && user.role === 'authorizer';
  };

  return (
    <div className="main-container">
      {!isAuthorizer() && (
        <div className="alert alert-warning">
          You don't have permission to authorize transactions
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}
      
      {/* Example Edit Modal */}
      {showDetails && selectedTransaction && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Transaction</h3>
              <button className="close-btn" onClick={() => setShowDetails(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Numeric Input Fields Example */}
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="text"
                  name="amount"
                  value={editForm.amount || ''}
                  onChange={handleNumericInput}
                  className="form-control"
                  placeholder="Enter amount"
                />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input
                  type="text"
                  name="price"
                  value={editForm.price || ''}
                  onChange={handleNumericInput}
                  className="form-control"
                  placeholder="Enter price"
                />
              </div>
              <div className="form-group">
                <label>Principal Amount</label>
                <input
                  type="text"
                  name="principal_amount"
                  value={editForm.principal_amount || ''}
                  onChange={handleNumericInput}
                  className="form-control"
                  placeholder="Enter principal amount"
                />
              </div>
              <div className="form-group">
                <label>Maturity Value</label>
                <input
                  type="text"
                  name="maturity_value"
                  value={editForm.maturity_value || ''}
                  onChange={handleNumericInput}
                  className="form-control"
                  placeholder="Enter maturity value"
                />
              </div>
              {/* Add more numeric fields as needed */}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => {
                const sanitized = getSanitizedEditForm();
                // Submit sanitized data to backend here
                setShowDetails(false);
              }}>Save</button>
              <button className="btn btn-secondary" onClick={() => setShowDetails(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Transaction Details Modal */}
      {showDetails && selectedTransaction && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Transaction Details</h3>
              <button className="close-btn" onClick={() => setShowDetails(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="transaction-details">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-item">
                    <div className="detail-label">Transaction ID:</div>
                    <div className="detail-value">{selectedTransaction.id}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Date:</div>
                    <div className="detail-value">{formatDate(selectedTransaction.date)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Description:</div>
                    <div className="detail-value">{selectedTransaction.description || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Amount:</div>
                    <div className={`detail-value ${parseFloat(selectedTransaction.amount) < 0 ? 'text-red' : 'text-green'}`}>
                      {parseFloat(selectedTransaction.amount) < 0 ? '-' : '+'}{formatNumberWithCommas(Math.abs(parseFloat(selectedTransaction.amount)), 4)} {selectedTransaction.currency || 'LKR'}
                    </div>
                  </div>
                </div>
                
                <div className="detail-section">
                  <h4>Additional Information</h4>
                  <div className="detail-item">
                    <div className="detail-label">Account:</div>
                    <div className="detail-value">{selectedTransaction.source_account || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Security:</div>
                    <div className="detail-value">{selectedTransaction.security_name || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Created At:</div>
                    <div className="detail-value">{formatDate(selectedTransaction.created_at)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Status:</div>
                    <div className="detail-value">
                      <span className={`status-badge status-${selectedTransaction.status}`}>
                        {selectedTransaction.status.charAt(0).toUpperCase() + selectedTransaction.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  {selectedTransaction.comment && (
                    <div className="detail-item">
                      <div className="detail-label">Comment:</div>
                      <div className="detail-value">{selectedTransaction.comment}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {selectedTransaction.status === 'pending' && isAuthorizer() && (
                <>
                  <div className="form-group" style={{ width: '100%', marginBottom: '15px' }}>
                    <label>Comment (required for rejection)</label>
                    <textarea 
                      value={comment} 
                      onChange={(e) => setComment(e.target.value)}
                      className="form-control"
                      rows="3"
                      placeholder="Enter reason for rejection"
                    />
                  </div>
                  <div className="action-buttons">
                    <button
                      className="btn btn-success"
                      onClick={() => {
                        handleAuthorization(selectedTransaction.id, 'approved');
                        setShowDetails(false);
                      }}
                      disabled={isLoading}
                    >
                      Approve
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        handleAuthorization(selectedTransaction.id, 'rejected');
                        setShowDetails(false);
                      }}
                      disabled={isLoading || !comment}
                    >
                      Reject
                    </button>
                  </div>
                </>
              )}
              {selectedTransaction.status !== 'pending' && isAuthorizer() && (
                <button
                  className="btn btn-warning"
                  onClick={() => handleAuthorization(selectedTransaction.id, 'pending')}
                  disabled={isLoading}
                >
                  Reset to Pending
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowDetails(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="transactions-container">
        <div className="transactions-header">
          <h2>Transactions</h2>
          <button 
            className="btn btn-secondary" 
            onClick={fetchTransactions} 
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        
        <div className="transactions-table-container">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Description</th>
                <th>Account</th>
                <th>Security</th>
                <th className="text-right">Amount</th>
                <th>Currency</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">No transactions found.</td>
                </tr>
              ) : (
                transactions.map((tx, idx) => (
                  <tr key={tx.deal_number || idx} className={tx.status === 'rejected' ? 'rejected-row' : ''}>
                    <td>{tx.id}</td>
                    <td>{formatDate(tx.date)}</td>
                    <td>{tx.description || 'N/A'}</td>
                    <td>{tx.source_account || 'N/A'}</td>
                    <td>{tx.security_name || 'N/A'}</td>
                    <td className={`text-right ${parseFloat(tx.amount) < 0 ? 'text-red' : 'text-green'}`}>
                      {parseFloat(tx.amount) < 0 ? '-' : '+'}{formatNumberWithCommas(Math.abs(parseFloat(tx.amount)), 4)}
                    </td>
                    <td>{tx.currency || 'N/A'}</td>
                    <td>
                      <span className={`status-badge status-${tx.status}`}>
                        {tx.status ? (tx.status.charAt(0).toUpperCase() + tx.status.slice(1)) : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleViewDetails(tx)}
                        >
                          Details
                        </button>
                        
                        {isAuthorizer() && (!tx.status || tx.status === 'pending') && (
                          <>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleAuthorization(tx.id, 'approved')}
                              disabled={isLoading}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleAuthorization(tx.id, 'rejected')}
                              disabled={isLoading}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AuthorizerPage;
